import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAdmin } from "@/lib/auth/middleware";
import * as db from "@/lib/db.server";

const RequestTypeSchema = z.enum(["claim", "report", "update", "removal", "is_this_you"]);

/**
 * Public: submit a provider correction / claim / removal request.
 * Handler-side validation + rate limit (5/hour per email+provider).
 */
export const submitProviderRequest = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z
      .object({
        providerId: z.string().uuid(),
        requestType: RequestTypeSchema,
        name: z.string().trim().min(1).max(200).optional(),
        email: z.string().trim().email().max(255).optional(),
        phone: z.string().trim().max(40).optional(),
        message: z.string().trim().max(4000).optional(),
        changes: z.record(z.string(), z.unknown()).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    // Rate limit: max 5 requests in the past hour for this email+provider combo.
    if (data.email) {
      const since = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const countRow = await db.one<{ count: number }>(
        `SELECT count(*)::int AS count
           FROM public.provider_update_requests
          WHERE provider_id = $1
            AND submitted_by_email = $2
            AND created_at >= $3`,
        [data.providerId, data.email.toLowerCase(), since],
      );
      if ((countRow?.count ?? 0) >= 5) {
        throw new Error("Too many requests. Please try again later.");
      }
    }

    await db.execute(
      `INSERT INTO public.provider_update_requests
         (provider_id, request_type, submitted_by_name, submitted_by_email,
          submitted_by_phone, message, requested_changes_json, status)
       VALUES ($1, $2, $3, $4, $5, $6, $7, $8)`,
      [
        data.providerId,
        data.requestType,
        data.name ?? null,
        data.email ? data.email.toLowerCase() : null,
        data.phone ?? null,
        data.message ?? null,
        JSON.stringify(data.changes ?? {}),
        "new",
      ],
    );
    return { ok: true };
  });

/** Admin: list update requests using cursor pagination (no count:"exact"). */
export const listProviderRequests = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .inputValidator((input) =>
    z
      .object({
        status: z.enum(["new", "under_review", "approved", "rejected", "completed"]).optional(),
        requestType: RequestTypeSchema.optional(),
        limit: z.number().int().min(1).max(100).default(50),
        cursor: z
          .object({ created_at: z.string(), id: z.string().uuid() })
          .nullable()
          .optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const conditions: string[] = [];
    const params: unknown[] = [];

    // Keyset cursor: rows strictly "after" (older than) the cursor in
    // (created_at DESC, id DESC) order.
    if (data.cursor) {
      params.push(data.cursor.created_at);
      const createdAtParam = `$${params.length}`;
      params.push(data.cursor.id);
      const idParam = `$${params.length}`;
      conditions.push(
        `(created_at < ${createdAtParam} OR (created_at = ${createdAtParam} AND id < ${idParam}))`,
      );
    }
    if (data.status) {
      params.push(data.status);
      conditions.push(`status = $${params.length}`);
    }
    if (data.requestType) {
      params.push(data.requestType);
      conditions.push(`request_type = $${params.length}`);
    }

    const whereClause = conditions.length ? `WHERE ${conditions.join(" AND ")}` : "";
    // +1 to detect hasMore
    params.push(data.limit + 1);
    const limitParam = `$${params.length}`;

    const all = await db.query(
      `SELECT *
         FROM public.provider_update_requests
         ${whereClause}
        ORDER BY created_at DESC, id DESC
        LIMIT ${limitParam}`,
      params,
    );

    const hasMore = all.length > data.limit;
    return { rows: hasMore ? all.slice(0, data.limit) : all, hasMore };
  });


/** Admin: change status / notes on a request. */
export const updateProviderRequest = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .inputValidator((input) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["new", "under_review", "approved", "rejected", "completed"]).optional(),
        adminNotes: z.string().max(4000).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const sets: string[] = ["reviewed_at = $1", "reviewed_by = $2"];
    const params: unknown[] = [new Date().toISOString(), context.userId];

    if (data.status) {
      params.push(data.status);
      sets.push(`status = $${params.length}`);
    }
    if (data.adminNotes !== undefined) {
      params.push(data.adminNotes);
      sets.push(`admin_notes = $${params.length}`);
    }

    params.push(data.id);
    await db.execute(
      `UPDATE public.provider_update_requests
          SET ${sets.join(", ")}
        WHERE id = $${params.length}`,
      params,
    );
    return { ok: true };
  });
